import * as React from 'react';
import { observer } from 'mobx-react';
import { Button } from '@ixicare/ui';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { useObserverAlertMode } from '~hooks/useObserverAlertMode';
import styles from './StartHandlingHereButton.module.css';

interface StartHandlingHereButtonProps {
  className?: string;
}

/**
 * The single way out of view-only, for the explainer modal.
 *
 * Deliberately not the top bar's DutyControl: that control answers "where am I
 * on duty, and how am I doing" — which is precisely the question the modal
 * isn't asking. Someone reading it wants one thing, to act on the alarms in
 * front of them, so this offers exactly that and nothing else. Where they are
 * currently on duty is already said in the modal's text, and shown in the bar.
 *
 * Expands into a group list only when the site is segmented and there is a real
 * choice to make — otherwise a single click is the whole interaction.
 */
const StartHandlingHereButton: React.FC<StartHandlingHereButtonProps> = observer(function StartHandlingHereButton({
  className,
}) {
  const { sites } = useStores();
  const { groups, isUnsegmentedSite, isOnDuty, onDutyGroupName, selectedSiteUuid, activate, deactivate } =
    useObserverAlertMode();
  const [busy, setBusy] = React.useState(false);
  const [picking, setPicking] = React.useState(false);

  const start = React.useCallback(
    async (groupId: string | null) => {
      setBusy(true);
      try {
        // Two duties can't be held at once, and only one of the two ways in
        // releases the old one on its own. Joining a group reassigns you, so
        // that moves duty by itself; switching on handling for an unsegmented
        // site only sets a local flag, and the group you are still in would
        // keep winning as your duty site. A no-group duty likewise has to be
        // cleared by hand or its flag lingers behind the new one.
        const onNoGroupDuty = isOnDuty && !onDutyGroupName;
        if (groupId === null || onNoGroupDuty) await deactivate();
        await activate(groupId);
      } finally {
        setBusy(false);
      }
    },
    [activate, deactivate, isOnDuty, onDutyGroupName]
  );

  // Groups for the viewed site haven't landed yet. Offering the button would
  // mean guessing which of the two actions it performs.
  const groupsUnknown = !isUnsegmentedSite && groups.length === 0;

  const handleClick = () => {
    if (isUnsegmentedSite) return void start(null);
    if (groups.length === 1) return void start(groups[0].id);
    setPicking((p) => !p);
  };

  return (
    <div className={className}>
      <Button onClick={handleClick} disabled={busy || groupsUnknown || !selectedSiteUuid} loading={busy} isBlock>
        {t('alertMode.startHandlingHere')}
      </Button>

      {picking && groups.length > 1 && (
        <div className={styles.Groups}>
          {/* Names what picking a row commits you to — the rows are only
                group names, which don't say where. */}
          <div className={styles.GroupsLabel}>
            {t('alertMode.handleAlarmsAt', { site: sites.selectedSite?.name ?? '' })}
          </div>
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              className={styles.GroupRow}
              disabled={busy}
              onClick={() => void start(group.id)}
            >
              {group.hcpGroupName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default StartHandlingHereButton;
